import React from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '../ui';
import { ArrowDropDownIcon, SearchIcon } from '../icons';

export interface PagedTableColumn<T> {
  key: string;
  label: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string | number;
  render?: (row: T) => React.ReactNode;
  /** Opt-in sort affordance on the header. Sort state itself is controlled by
   *  the parent via the `sortBy` / `sortDir` / `onSortChange` props. */
  sortable?: boolean;
}

export type SortDirection = 'asc' | 'desc';

export interface PagedTableProps<T> {
  columns: PagedTableColumn<T>[];
  rows: T[];
  totalCount: number;
  page: number;        // 1-based
  pageSize: number;
  loading: boolean;
  rowKey: (row: T) => string | number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRowClick?: (row: T) => void;

  search?: string;
  searchPlaceholder?: string;
  onSearchChange?: (term: string) => void;
  /** Rendered to the left of the search bar on the same row. */
  filtersSlot?: React.ReactNode;

  emptyMessage?: string;
  pageSizeOptions?: number[];

  /** Opt-in row selection. When true, renders a checkbox column. Selection
   *  state is controlled by the parent via `selectedIds` / `onSelectionChange`. */
  selectable?: boolean;
  selectedIds?: (string | number)[];
  onSelectionChange?: (ids: (string | number)[]) => void;

  /** Controlled sort state. The header clicks call `onSortChange` with the
   *  column key + next direction; the parent is responsible for re-fetching
   *  in sorted order. Columns must opt in via `column.sortable`. */
  sortBy?: string;
  sortDir?: SortDirection;
  onSortChange?: (key: string, dir: SortDirection) => void;

  /** When true, the table claims its parent's full height and only the
   *  table body scrolls — column headers and pagination stay pinned.
   *  Requires the parent layout chain to provide a constrained height. */
  stickyHeader?: boolean;
}

export default function PagedTable<T>(props: PagedTableProps<T>) {
  const {
    columns,
    rows,
    totalCount,
    page,
    pageSize,
    loading,
    rowKey,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    search,
    searchPlaceholder = 'Search...',
    onSearchChange,
    filtersSlot,
    emptyMessage = 'No results found',
    pageSizeOptions = [10, 25, 50, 100],
    selectable = false,
    selectedIds = [],
    onSelectionChange,
    sortBy,
    sortDir = 'desc',
    onSortChange,
    stickyHeader = false,
  } = props;

  // Clicking a sortable header toggles direction on the same column, or
  // starts a fresh DESC sort when targeting a new column.
  const handleSortClick = (key: string) => {
    if (!onSortChange) return;
    const nextDir: SortDirection = sortBy === key && sortDir === 'desc' ? 'asc' : 'desc';
    onSortChange(key, nextDir);
  };

  const showSearch = typeof onSearchChange === 'function';
  const showFiltersRow = showSearch || filtersSlot != null;

  // Selection helpers — Sets give O(1) lookups for the rendering loop and
  // the row click handler. We rebuild on each render rather than memoizing
  // because the array reference can change frequently from the parent.
  const selectedSet = new Set<string | number>(selectedIds);
  const pageRowKeys = rows.map(rowKey);
  const allOnPageSelected = pageRowKeys.length > 0 && pageRowKeys.every((k) => selectedSet.has(k));
  const someOnPageSelected = pageRowKeys.some((k) => selectedSet.has(k)) && !allOnPageSelected;

  const toggleRow = (key: string | number) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(Array.from(next));
  };

  const toggleAllOnPage = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (allOnPageSelected) {
      pageRowKeys.forEach((k) => next.delete(k));
    } else {
      pageRowKeys.forEach((k) => next.add(k));
    }
    onSelectionChange(Array.from(next));
  };

  return (
    <Box
      sx={
        stickyHeader
          ? {
              // Fill the parent's height so the body can scroll internally
              // while the page above stays put. Parent must give us a
              // constrained height; `minHeight: 0` is essential for the
              // flex chain to actually allow shrinking.
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }
          : undefined
      }
    >
      {showFiltersRow && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {filtersSlot}
          {showSearch && (
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={search ?? ''}
              onChange={(e) => onSearchChange!(e.target.value)}
              sx={{ flex: 1, minWidth: 240, maxWidth: 600 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      )}

      <Paper
        sx={
          stickyHeader
            ? {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <TableContainer
          sx={
            stickyHeader
              ? {
                  flex: 1,
                  minHeight: 0,
                  // Vertical scroll for the row list; horizontal is
                  // suppressed because the table itself is forced to
                  // fit the container via `tableLayout: fixed` below.
                  // Without this clamp, narrow viewports get a long
                  // horizontal scrollbar instead of the table breathing.
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }
              : undefined
          }
        >
          <Table
            stickyHeader={stickyHeader}
            // Fixed layout + full width = columns share the container
            // budget instead of growing past it. Honors each column's
            // `width` prop as a hint; columns without one split the
            // remaining space proportionally. Text in cells truncates
            // (or wraps, per the cell's own sx) instead of forcing a
            // page-level horizontal scroll.
            sx={stickyHeader ? { tableLayout: 'fixed', width: '100%' } : undefined}
          >
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'grey.100',
                  '& .MuiTableCell-root': {
                    fontWeight: 600,
                    color: 'text.primary',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    whiteSpace: 'nowrap',
                    // Stick the cell backgrounds opaque so rows can't
                    // bleed through when sticky-scrolled.
                    bgcolor: 'grey.100',
                  },
                }}
              >
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected}
                      onChange={toggleAllOnPage}
                      inputProps={{ 'aria-label': 'Select all rows on this page' }}
                    />
                  </TableCell>
                )}
                {columns.map((c) => {
                  const isActive = c.sortable && sortBy === c.key;
                  return (
                    <TableCell
                      key={c.key}
                      align={c.align ?? 'left'}
                      sortDirection={isActive ? sortDir : false}
                      sx={c.width != null ? { width: c.width } : undefined}
                    >
                      {c.sortable && onSortChange ? (
                        <TableSortLabel
                          active={isActive}
                          direction={isActive ? sortDir : 'desc'}
                          // Filled down-triangle (▼) instead of MUI's default
                          // arrow. MUI rotates the icon 180° for ascending,
                          // so this becomes ▲ when sorted A→Z.
                          IconComponent={ArrowDropDownIcon}
                          onClick={() => handleSortClick(c.key)}
                          // Show the triangle on inactive sortable columns at
                          // a dim opacity so it's clear they're sortable —
                          // MUI's default is opacity 0 until hover, which
                          // makes only the active column read as sortable.
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              opacity: isActive ? 1 : 0.4,
                            },
                            '&:hover .MuiTableSortLabel-icon': {
                              opacity: isActive ? 1 : 0.7,
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      ) : (
                        c.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const isSelected = selectedSet.has(key);
                  return (
                    <TableRow
                      key={key}
                      hover={!!onRowClick}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      selected={isSelected}
                      sx={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        // Whisper-quiet zebra striping for scannability.
                        // Even rows get a barely-visible grey tint that
                        // sits behind the hover and selection layers
                        // (which use heavier opacities), so the active
                        // affordances still read as the dominant state.
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(0,0,0,0.018)',
                        },
                      }}
                    >
                      {selectable && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleRow(key)}
                            inputProps={{ 'aria-label': `Select row ${key}` }}
                          />
                        </TableCell>
                      )}
                      {columns.map((c) => (
                        <TableCell key={c.key} align={c.align ?? 'left'}>
                          {c.render ? c.render(row) : (row as any)[c.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={totalCount === 0 ? 0 : Math.max(0, page - 1)}
          onPageChange={(_, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={pageSizeOptions}
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -2px 4px rgba(0,0,0,0.04)',
            zIndex: 1,
          }}
        />
      </Paper>
    </Box>
  );
}
